import React from 'react';
import { Link } from 'react-router-dom';
import { AppShot } from '../components/AppShot';

const facts = [
  {
    title: 'Five views of the same work',
    text: 'Board, list, timeline, calendar, and workload. Status runs from New through In Progress, In Review, Blocked, Completed, and Cancelled.',
  },
  {
    title: 'Dimensions, not one tag pile',
    text: 'Each dimension has its own tags. The demo organization uses Business Units, Category, and Priority.',
  },
  {
    title: 'Hours and a budget on the workspace',
    text: 'A timer, manual entries, a billable flag, and a budget panel that compares burn with the estimate.',
  },
  {
    title: 'An assistant that waits',
    text: 'It can list blocked work, summarize a workspace, or draft a client status. Changes wait for approval.',
  },
];

export const Home: React.FC = () => {
  return (
    <div>
      <section className="pb-16 pt-28 lg:pb-20 lg:pt-32">
        <div className="container-width section-padding">
          <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] lg:gap-12">
            <div>
              <p className="eyebrow">Workspaces for delivery teams</p>
              <h1 className="font-display text-4xl text-ink sm:text-5xl lg:text-[3.35rem]">
                The work, the write-up, and the hours.
              </h1>
              <p className="lede mt-5">
                SynqTag keeps work items, the pages that explain them, time entries, and a portfolio
                across workspaces. You classify work with dimensions. The assistant drafts the status
                update and asks before it changes anything.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="https://app.synqtag.com/auth/register" className="btn-premium">
                  Start free
                </a>
                <Link to="/product-tour" className="btn-glass">
                  Look through the product
                </Link>
              </div>
              <p className="mt-4 text-sm text-ink-soft">
                Individual is free for one person. Team is $9.95 per person each month.
              </p>
            </div>
            <AppShot
              priority
              src="/images/app/home.jpg"
              alt="SynqTag home: greeting, status counts, and a list of work items across workspaces"
              caption="Home in the demo organization, on the Forest theme. October 2026."
            />
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-white py-16">
        <div className="container-width section-padding">
          <div className="grid gap-8 sm:grid-cols-2">
            {facts.map((fact) => (
              <div key={fact.title} className="border-t border-line pt-5">
                <h2 className="font-display text-2xl">{fact.title}</h2>
                <p className="mt-2 text-ink-soft">{fact.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-width section-padding">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="eyebrow">A workspace</p>
              <h2 className="font-display text-3xl sm:text-4xl">One engagement, with the budget on the same page as the board.</h2>
              <p className="lede mt-4">
                Open a workspace and you get the counts, the budget and utilization, then the work.
                The Acme Insurance demo is a policy portal: nine items, tags for Insurance, Sales, and
                IT Cell, and a board with New, In Progress, In Review, and Blocked.
              </p>
              <Link to="/features" className="mt-6 inline-flex min-h-11 items-center text-sm font-semibold text-forest">
                Read the feature list
              </Link>
            </div>
            <AppShot
              src="/images/app/board.jpg"
              alt="Acme Insurance workspace with budget, hours, and the start of a kanban board"
              caption="Workspace detail from the demo. Budget is unset, so remaining shows the billable burn as a negative."
            />
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-width section-padding">
          <div className="mb-10 max-w-2xl">
            <p className="eyebrow">Across the organization</p>
            <h2 className="font-display text-3xl sm:text-4xl">Four workspaces, one portfolio.</h2>
            <p className="lede mt-4">
              Portfolio rolls up open items, overdue work, hours against estimate, and budget burn.
              A weekly email goes out Mondays at 07:00 UTC. The demo has four workspaces and 28 open items.
            </p>
          </div>
          <AppShot
            src="/images/app/portfolio.jpg"
            alt="Portfolio table showing four demo workspaces, health, open items, overdue counts, and hours"
            caption="Portfolio in the demo organization. Every workspace is marked at risk because overdue items are high."
          />
        </div>
      </section>

      <section className="py-20">
        <div className="container-width section-padding grid items-center gap-10 lg:grid-cols-2">
          <AppShot
            src="/images/app/assistant.jpg"
            alt="Assistant empty state with prompts to summarize a workspace, draft a client status, and list blocked work"
            caption="The assistant, scoped to the Acme Insurance workspace."
          />
          <div>
            <p className="eyebrow">Assistant</p>
            <h2 className="font-display text-3xl sm:text-4xl">Ask about this workspace. Approve the change.</h2>
            <ul className="mt-5 space-y-3 text-ink-soft">
              <li>Summarize blocked items, overdue work, and who is overloaded.</li>
              <li>Draft this week’s client status. It does not send the email.</li>
              <li>List overdue items assigned to you, or every blocked item.</li>
              <li>Work items and pages it touches collect in the session rail.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-sidebar py-16 text-white">
        <div className="container-width section-padding flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-3xl text-white sm:text-4xl">Start on the free plan.</h2>
            <p className="mt-3 max-w-xl text-primary-200">
              One person, two workspaces, five dimensions, unlimited work items. Move to Team when
              you need more people, time tracking, and automation.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href="https://app.synqtag.com/auth/register" className="btn-on-dark">
              Create an account
            </a>
            <Link to="/pricing" className="btn-ghost-on-dark">
              Compare plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
